import { Autocomplete, Avatar, Box, Button, Card, Divider, Grid, InputLabel, OutlinedInput, Stack, Switch, TextField, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import GoBackButton from "../../components/go-back-button";
import { useEffect, useState } from "react";
import { ICryptoCurrency } from "../../interfaces/crypto-currency/ICryptoCurrency";
import Loader from "../../components/loader";
import { useNotification } from "../../components/notification/useNotification";
import { getBaseURLWithAuth } from "../../common/utils/session";
import extractErrorMessage from "../../common/utils/errorMessages";
import useLazyLoading from "../../common/hooks/loader";
import { tryCreateCryptoCurrencyUser, tryGetCryptoCurrencyList, tryUpdateCryptoCurrencyList } from "../../services/crypto-currency/cripto-currency.service";

export default function CreateOrEditCoin() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const showLoading = useLazyLoading(loading);
    const { displayNotification } = useNotification();
    const [cryptoCurrencyList, setCryptoCurrencyList] = useState<ICryptoCurrency[]>([]);
    const [cryptoCurrency, setCryptoCurrency] = useState<ICryptoCurrency | null>(null);
    const [inputValue, setInputValue] = useState("");
    const [debouncedValue, setDebouncedValue] = useState("");
    const [open, setOpen] = useState(false);
    const [isStable, setIsStable] = useState(false);

    useEffect(() => {
        setLoading(true);
        async function getCryptoCurrencyList() {
            try {
                var response = (await tryGetCryptoCurrencyList()).data;
                setCryptoCurrencyList(response);
            }
            catch {
                displayNotification({ type: 'error', message: 'Não foi possível obter a lista de Crypto Moedas.' });
            }
            finally {
                setLoading(false);
            }
        }

        getCryptoCurrencyList();

    }, []);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(inputValue);
        }, 500);

        return () => clearTimeout(handler);
    }, [inputValue]);

    useEffect(() => {
        async function getCryptoCurrencyWithCurrencyNameList(debouncedValue: string) {
            try {
                var response = (await tryGetCryptoCurrencyList(debouncedValue)).data;
                setOpen(true);

                if (response.length > 0) {
                    setCryptoCurrencyList(response);
                }
            }
            catch {
                displayNotification({ type: 'error', message: 'Não foi possível obter a lista de Crypto Moedas.' });
            }
            finally {
                setLoading(false);
            }
        }

        if (debouncedValue.length >= 3 && !existsOnCryptoList(debouncedValue)) {
            setLoading(true);
            getCryptoCurrencyWithCurrencyNameList(debouncedValue);
        } 

    }, [debouncedValue]);

    const existsOnCryptoList = (debouncedValue: string) => {
        const filteredList = cryptoCurrencyList.filter(cryptoCurrency => cryptoCurrency.name.toLowerCase().includes(debouncedValue.toLocaleLowerCase()));

        return filteredList.length > 0;
    }

    const handleSubmit = async (event: any) => {
        event.preventDefault();
        setLoading(true);
        try {
            var response = await tryCreateCryptoCurrencyUser(cryptoCurrency!);

            if (response?.status === 201) {
                displayNotification({ type: 'success', message: 'Crypto Moeda criada com sucesso.' })
                navigate(getBaseURLWithAuth() + 'cryptocurrency');
            }
        } catch (error) {
            displayNotification({ type: 'error', message: extractErrorMessage(error) })
        }
        finally {
            setLoading(false);
        }
    }

    const updateCryptoCurrencyList = async () => {
        setLoading(true);

        try {
            var response = await tryUpdateCryptoCurrencyList();

            if (response?.status === 200) {
                displayNotification({ type: 'success', message: 'Lista de Moedas actualizada com sucesso.' });

                setTimeout(function () {
                    window.location.reload();
                }, 5000);
            }
        } catch (error) {
            displayNotification({ type: 'error', message: 'Não foi possível actualizar a lista de moedas.' });
        } finally {
            setLoading(false);
        }
    }

    const isStableHandleOnChange = (cryptoCurrency: ICryptoCurrency) => {
        const newCryptoCurrency = cryptoCurrency;
        newCryptoCurrency.isStable = !newCryptoCurrency.isStable;

        setCryptoCurrency(newCryptoCurrency);
        setIsStable(newCryptoCurrency.isStable);
    }

    return (
        <>
            {showLoading === true ? <Loader /> : null}
            <Box mt={3} mb={1}>
                <Box mb={1.5}>
                    <GoBackButton />
                </Box>
                <Grid container justifyContent="space-between" alignItems="center" spacing={1}>
                    <Grid>
                        <Typography variant="h4" fontWeight={700} sx={{ color: 'primary.main', letterSpacing: 1 }}>
                            Criar Moeda
                        </Typography>
                    </Grid>
                    <Grid>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={() => updateCryptoCurrencyList()}
                        >
                            Actualizar Lista Moedas
                        </Button>
                    </Grid>
                </Grid>
                <Divider sx={{ mt: 2, borderColor: 'rgba(255,215,0,0.15)' }} />
            </Box>
            <Card sx={{ mt: 2, border: '1px solid rgba(255,215,0,0.15)' }}>
                <Box>
                    <Autocomplete
                        disablePortal
                        getOptionLabel={(option) => option.name}
                        options={cryptoCurrencyList}
                        loading={loading}
                        open={open}
                        onOpen={() => setOpen(true)}
                        onClose={() => setOpen(false)}
                        renderInput={(params) => <TextField {...params} label="Lista de Crypto Moedas" />}
                        renderOption={(props, option) => (
                            <li {...props} key={option.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <Avatar src={option.imageUrl} alt={option.name} sx={{ width: 30, height: 30 }} />
                                {option.name}
                            </li>
                        )}
                        onChange={(event, newValue) => setCryptoCurrency(newValue)}
                        onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
                    />
                </Box>
            </Card>
            {cryptoCurrency &&
                <Box>
                    <Grid margin={5}>
                        <Stack spacing={1}>
                            <Grid container spacing={4} alignItems="center">
                                <Grid sx={{ flex: 1 }}>
                                    <InputLabel htmlFor="input-currency-name" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>Moeda</InputLabel>
                                    <OutlinedInput
                                        id="input-currency-name"
                                        disabled
                                        name="input-currency-name"
                                        value={cryptoCurrency?.name}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid sx={{ flex: 1 }}>
                                    <InputLabel htmlFor="input-currency-price" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>Preço</InputLabel>
                                    <OutlinedInput
                                        id="input-currency-price"
                                        disabled
                                        name="input-currency-price"
                                        value={cryptoCurrency?.price}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid sx={{ flex: 1 }}>
                                    <InputLabel htmlFor="input-price-change" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>Variação Preço 24h</InputLabel>
                                    <OutlinedInput
                                        id="input-price-change"
                                        disabled
                                        name="input-price-change"
                                        value={cryptoCurrency?.priceChangePercentage24h + "%"}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid sx={{ flex: 1 }}>
                                    <InputLabel htmlFor="input-currency-symbol" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>Nome Abreviado</InputLabel>
                                    <OutlinedInput
                                        id="input-currency-symbol"
                                        disabled
                                        name="input-currency-symbol"
                                        value={cryptoCurrency?.abbreviation}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid sx={{ flex: 1 }}>
                                    <InputLabel sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                        Stable Coin
                                    </InputLabel>
                                    <Switch checked={isStable} onChange={() => isStableHandleOnChange(cryptoCurrency)} />
                                </Grid>
                            </Grid>
                        </Stack>
                    </Grid>
                    <Grid margin={5}>
                        <Button
                            color="primary"
                            size="large"
                            variant="contained"
                            fullWidth
                            onClick={(event) => handleSubmit(event)}
                        >
                            Criar
                        </Button>
                    </Grid>
                </Box>
            }
        </>
    )
}